import { Menu, MenuRecipe, Recipe, RecipeUUID, RecipeCoreId } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'

// Re-export types from menu-service for backwards compatibility
export type { Recipe, RecipeUUID, RecipeCoreId }

export type MenuRecipesState = {
  recipesV3: {
    recipes: Record<RecipeUUID, Recipe>
    coreIdToUUID: Record<RecipeCoreId, RecipeUUID>
  }
}

const recipesInitialState: MenuRecipesState['recipesV3'] = {
  recipes: {},
  coreIdToUUID: {},
}

type Action = {
  type: typeof actionTypes.MENU_SERVICE_V3_DATA_RECEIVED
  response: Menu
}

const fromAPIRecipe = (recipe: MenuRecipe): Recipe => ({
  id: recipe.id as RecipeUUID,
  coreId: recipe.core_recipe_id as RecipeCoreId,
  name: recipe.name,
  dietaryClaims: recipe.dietary_claims,
  fiveADay: recipe.five_a_day,
  foodBrand: recipe.food_brand,
  images: recipe.images,
  invalidPreference: recipe.invalid_preference,
  isAvailable: recipe.is_available,
  merchandisingTags: {
    availability: recipe.merchandising_tags.availability
      ? {
          name: recipe.merchandising_tags.availability.name,
          color: recipe.merchandising_tags.availability.color,
          backgroundColor: recipe.merchandising_tags.availability.background_color,
        }
      : null,
    tagline: recipe.merchandising_tags.tagline
      ? {
          name: recipe.merchandising_tags.tagline.name,
          color: recipe.merchandising_tags.tagline.color,
          backgroundColor: recipe.merchandising_tags.tagline.background_color,
        }
      : null,
  },
  nutritionalInformation: {
    perPortion: {
      energyKcal: recipe.nutritional_information.per_portion.energy_kcal,
      carbsMg: recipe.nutritional_information.per_portion.carbs_mg,
      fatMg: recipe.nutritional_information.per_portion.fat_mg,
      proteinMg: recipe.nutritional_information.per_portion.protein_mg,
      saltMg: recipe.nutritional_information.per_portion.salt_mg,
    },
  },
  prepTime: recipe.prep_time,
  rating: recipe.rating,
  shelfLife: recipe.shelf_life
    ? {
        maxDays: recipe.shelf_life.max_days,
        minDays: recipe.shelf_life.min_days,
      }
    : null,
  spiceLevel: recipe.spice_level,
  surcharge: recipe.surcharge
    ? {
        name: recipe.surcharge.name,
        price: recipe.surcharge.price,
        pricePerPortion: recipe.surcharge.price_per_portion,
      }
    : null,
})

export const menuRecipesReducers = {
  recipesV3: (state = recipesInitialState, action: Action): MenuRecipesState['recipesV3'] => {
    switch (action.type) {
      case actionTypes.MENU_SERVICE_V3_DATA_RECEIVED: {
        const domainRecipes = Object.values(action.response.recipes).map(fromAPIRecipe)

        const coreIdToUUID: Record<RecipeCoreId, RecipeUUID> = { ...state.coreIdToUUID }
        domainRecipes.forEach((recipe) => {
          coreIdToUUID[recipe.coreId] = recipe.id
        })

        const recipes = Object.fromEntries(
          Object.entries(state.recipes)
            // take existing recipes, mark them as unavailable
            .map(([id, recipe]) => [id, { ...recipe, isAvailable: false }])
            // add newly loaded recipes (add them at the end to overwrite existing recipes with Object.fromEntries)
            .concat(domainRecipes.map((recipe) => [recipe.id, recipe])),
        )

        return {
          coreIdToUUID,
          recipes,
        }
      }
      default:
        return state
    }
  },
}
